/**
 * Training videos (docs/TRAINING.md): built locally by `npm run training:build` (training/), uploaded to Storage
 * `training/<id>.mp4|vtt|jpg` by `npm run training:upload`. Read by signed-in users per storage.rules: the intro for
 * everyone, each role video for readers of `requires` (the same mapping as the rules — change both together).
 * `pages` = the screens whose "?" opens this video (longest matching prefix wins; "/" is the dashboard only).
 */
import type { PageKey } from './permissions.ts';

export type TrainingVideoId = 'intro' | 'gabbai' | 'secretary' | 'admin';

export interface TrainingVideo {
  id: TrainingVideoId;
  title: string;
  description: string;
  /** the page a user must be able to read to see the video; null = everyone */
  requires: PageKey | null;
  /** mm:ss, shown on the card (from the build report) */
  length: string;
  pages: string[];
  /** the phone video is portrait */
  portrait?: boolean;
}

export const TRAINING_VIDEOS: readonly TrainingVideo[] = [
  {
    id: 'intro', title: 'היכרות עם המערכת', requires: null, length: '3:28',
    description: 'מה המערכת עושה ואיך היא חוסכת ניירת: כרטיס המתפלל, הכרטסת, נדרים פלוס, מכירות ועליות, היומן, הכספים והדיוור.',
    pages: ['/'],
  },
  {
    id: 'gabbai', title: 'הגבאי — מכירות ועליות מהטלפון', requires: 'services', length: '1:59', portrait: true,
    description: 'רישום שבת מהטלפון: מכירות, עליות לפי התור וחיובים, חזנים, ודף השבת להדפסה.',
    pages: ['/services', '/print/sheet', '/minyanim', '/calendar'],
  },
  {
    id: 'secretary', title: 'המזכיר — כספים וכרטסות', requires: 'ledger', length: '2:48',
    description: 'כרטסת, חיוב ותשלום ידני, הוצאות, בקשת תשלום, הוראת קבע שנכשלה, דף הבנק ודוח התרומות השנתי.',
    pages: ['/people', '/ledger', '/payments', '/boxes', '/bank', '/reports', '/alerts', '/nedarim', '/wallets', '/supports', '/loans', '/events', '/tasks'],
  },
  {
    id: 'admin', title: 'המנהל — משתמשים, הרשאות והגדרות', requires: 'users', length: '2:09',
    description: 'משתמשים חדשים ותבניות, הרשאות לפי עמוד ופרטים אישיים, יומן השינויים, וההגדרות.',
    pages: ['/users', '/audit', '/settings'],
  },
];

export const trainingFile = (id: TrainingVideoId, ext: 'mp4' | 'vtt' | 'jpg') => `training/${id}.${ext}`;

/** The video for a screen (longest page prefix; "/" only for the dashboard itself), or null. */
export function videoForPath(pathname: string): TrainingVideo | null {
  let best: TrainingVideo | null = null, len = -1;
  for (const v of TRAINING_VIDEOS) {
    for (const p of v.pages) {
      const hit = p === '/' ? pathname === '/' : pathname === p || pathname.startsWith(`${p}/`);
      if (hit && p.length > len) { best = v; len = p.length; }
    }
  }
  return best;
}
